import { useHistory } from 'react-router-dom';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
	toggleCollapseDashboardNav,
	dashboardNavMounted,
	dashboardNavUnmounted,
} from 'reducers/uiStateReducer';
import { getFavoriteProperties } from 'data/reducers/propertyManagementReducer';
import DashboardNav from 'pages/_rd_dashboard/DashboardNav';
import { useUpgradeIncentive } from 'src/hooks/useUpgradeIncentive';

import './DashboardLayout.css';
import {
	checkPlanLimits,
	fetchCurrentSubscription,
} from 'data/reducers/subscriptionReducer';

/** Delay before the daily upgrade incentive, matching the old promo cadence. */
const DAILY_INCENTIVE_SHOW_DELAY_MS = 2000;

const DashboardLayout = ({ children }) => {
	const history = useHistory();
	const dispatch = useDispatch();

	const { showDailyIncentive, resetSession } = useUpgradeIncentive();

	let {
		isLoggedIn,
		isLoading,
		isUpdating,
		isCollapsed,
		currentSubscription,
		usageSummary,
		isLoadingCurrentSubscription,
	} = useSelector((state) => ({
		isLoggedIn: !!state.user.userData?.id,
		isLoading: state.user.isLoading,
		isUpdating: state.user.isUpdating,
		isCollapsed: state.uiState.isDashboardNavCollapsed,
		currentSubscription: state.subscription?.currentSubscription,
		isLoadingCurrentSubscription:
			state.subscription?.isLoadingCurrentSubscription,
		usageSummary: state.subscription?.usageSummary,
	}));

	const { favoriteProperties = [] } = useSelector(
		(state) => state.propertyManagement || {}
	);

	// Tell the app header this page has the collapsible sidebar, so it shows the toggle
	useEffect(() => {
		dispatch(dashboardNavMounted());
		return () => dispatch(dashboardNavUnmounted());
	}, [dispatch]);

	useEffect(() => {
		if (!isLoggedIn && !isLoading && !isUpdating) {
			history.replace('/sign-in');
		}
	}, [isLoggedIn, isLoading, isUpdating, history]);

	useEffect(() => {
		if (isLoggedIn) {
			dispatch(fetchCurrentSubscription());
		}
	}, [isLoggedIn, dispatch]);

	// Fetch favorite properties when user is logged in
	useEffect(() => {
		if (isLoggedIn && !isLoading && favoriteProperties?.length === 0) {
			dispatch(getFavoriteProperties());
		}
	}, [isLoggedIn, isLoading, dispatch, favoriteProperties?.length]);

	useEffect(() => {
		if (isLoggedIn && !isLoading && usageSummary?.limits) {
			dispatch(
				checkPlanLimits(usageSummary?.limits, 'usage', usageSummary?.limits)
			);
		}
	}, [usageSummary, isLoggedIn, isLoading, dispatch]);

	// Daily upgrade incentive for free users (free-month promo lives on marketing pages only)
	useEffect(() => {
		if (isLoggedIn && !isLoading && !isLoadingCurrentSubscription) {
			resetSession();

			if (currentSubscription === null) {
				const timer = setTimeout(() => {
					showDailyIncentive({
						trigger: 'dashboard_daily',
						source: 'DashboardLayout',
					});
				}, DAILY_INCENTIVE_SHOW_DELAY_MS);

				return () => clearTimeout(timer);
			}
		}
	}, [
		isLoggedIn,
		isLoading,
		currentSubscription,
		resetSession,
		showDailyIncentive,
		isLoadingCurrentSubscription,
	]);

	const toggleCollapse = () => {
		dispatch(toggleCollapseDashboardNav());
	};

	return (
		<div className="rd-dashboard">
			<DashboardNav isCollapsed={isCollapsed} toggleCollapse={toggleCollapse} />
			<div
				className={`rd-dashboard-content-wrapper ${
					isCollapsed ? 'is-collapsed' : ''
				}`}
			>
				<div className="rd-dashboard-content">{children}</div>
			</div>
		</div>
	);
};

export default DashboardLayout;
